import * as Yup from "yup";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import React, {useState} from "react";
import {useCreate, useNotify, useRefresh} from "react-admin";
import InputField2 from "../common/InputComponents/InputField2";
import TextInputPhone from "../DocumentAnalysis/components/inputComponents/TextInputPhone";
import InputSelectField from "../common/InputComponents/InputSelectField";
import { notifyPopupBlocked, openWithPopupCheck } from "../../utils/popupWindow";

export const ApplicationRegistrationForm = ({data, splitData}) => {
    // DocUpload and Guest Card copy links are legacy VR flows. Only regular
    // ATS links should enter the password/OTP registration flow.
    const isLegacyVrCopyUrl = /(?:^|\/)(?:docupload|guestcard)(?:\/|$)/i.test(
        window.location.pathname
    );

    // form validation schema
    const schema = Yup.lazy((value) =>
        Yup.object().shape({
            first_name: Yup.string()
                .typeError("First name is required!")
                // .max(75, "maximum 75 characters is required")
                // .matches(/^[A-Za-z0-9_ ,.'-]+$|^$/i, "Enter a valid name")
                // .matches(/^$|^\S+.*/g, "Enter a valid value")
                .required("First name is required!")
                .min(2, "Minimum 2 characters is required"),

            last_name: Yup.string()
                .typeError("Last name is required!")
                // .max(75, "maximum 75 characters is required")
                // .matches(/^[A-Za-z0-9_ ,.'-]+$|^$/i, "Enter a valid name")
                // .matches(/^$|^\S+.*/g, "Enter a valid value")
                .required("Last name is required!")
                .min(2, "Minimum 2 characters is required"),
            mobile: Yup.string()
                .required("Mobile number is required!")
                .typeError("Mobile number is required!")
                .test("rent", "Min 10 digits are required", (value) => {
                    let temp: any = value?.toString().replace(/\D/g, "");
                    return temp?.length > 10 ? true : false;
                }),
            email: Yup.string()
                .email("Invalid email format")
                // .max(75, "maximum 75 characters is required")
                .required("Email is required!")
                .matches(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/, "Invalid domain"),
            confirm_email: Yup.string()
                .oneOf([Yup.ref("email"), ""], "Email must match!")
                // .max(75, "maximum 75 characters is required")
                .required("Confirm Email is required!")
                .matches(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/, "Invalid domain"),
            password: isLegacyVrCopyUrl
                ? Yup.string().notRequired()
                : Yup.string()
                    .matches(
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
                        "Use 8 or more characters with a mix of letters, numbers & special characters"
                    )
                    .required("Password is required!"),
            confirm_password: isLegacyVrCopyUrl
                ? Yup.string().notRequired()
                : Yup.string()
                    .oneOf([Yup.ref("password"), ""], "Password must match!")
                    .required("Confirm Password is required!"),
            registration_otp: Yup.string().nullable(),
            office:
                splitData &&
                splitData?.length > 0 &&
                splitData?.includes("Agent") &&
                Yup.object()
                    .shape({
                        value: Yup.string().required("Required!").nullable(),
                        label: Yup.string().nullable(),
                    })

                    .nullable()
                    // .max(75, "maximum 75 characters is required")
                    .required("Required!"),
            property:
                data?.data?.records?.subscriberCompanyType === 'Property Management' &&
                Yup.object()
                    .shape({
                        value: Yup.string().required("Required!").nullable(),
                        label: Yup.string().nullable(),
                    })

                    .nullable()
                    // .max(75, "maximum 75 characters is required")
                    .required("Required!"),
            agent:
                splitData &&
                splitData?.length > 0 &&
                splitData?.includes("Office") &&
                Yup.object()
                    .shape({
                        value: Yup.string().required("Required!").nullable(),
                        label: Yup.string().nullable(),
                    })
                    .nullable()
                    // .max(75, "maximum 75 characters is required")
                    .required("Required!"),
        })
    );

    // Initial form values
    const defaultFormValues = {
        first_name: "",
        last_name: "",
        mobile_number: "",
        email: "",
        confirm_email: "",
        password: "",
        confirm_password: "",
        registration_otp: "",
        office: null,
        agent: null,
        property: {
            label: data?.data?.records?.property?.[0]?.PropertyName || "",
            value: data?.data?.records?.property?.[0]?.ID || ""
        }
    };

    const methods = useForm({
        defaultValues: defaultFormValues,
        resolver: yupResolver(schema),
        mode: "onBlur",
    });
    const refresh = useRefresh();
    const notify = useNotify();
    const [create, {isLoading}] = useCreate();
    const [registrationSessionToken, setRegistrationSessionToken] = useState<string | null>(null);
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpDestination, setOtpDestination] = useState("");
    const [existingApplications, setExistingApplications] = useState<any[]>([]);
    const [resultType, setResultType] = useState<"new" | "existing_single" | "existing_multiple">("new");
    const [selectedAction, setSelectedAction] = useState<"create_new" | "continue_existing" | "add_into_existing">("create_new");
    const [selectedApplicationNumber, setSelectedApplicationNumber] = useState<number | null>(null);
    const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);
    const getExistingApplicationSelectionId = (application: any) =>
        Number(
            application?.selectionId ||
            application?.applicantId ||
            application?.applicationNumber ||
            0
        );
    const selectedExistingApplication = existingApplications.find(
        (application) => {
            if (Number(selectedApplicationId || 0) > 0) {
                return getExistingApplicationSelectionId(application) === Number(selectedApplicationId);
            }

            return Number(application.applicationNumber) === Number(selectedApplicationNumber);
        }
    );
    const hasSelectedExistingApplication = Boolean(selectedExistingApplication);
    const selectedApplicationAllowsAddIntoExisting = Boolean(
        selectedExistingApplication?.allowedActions?.add_into_existing &&
        Number(selectedExistingApplication?.applicationStatusId || 0) >= 3
    );
    const requiresExistingApplicationSelection =
        selectedAction === "continue_existing" || selectedAction === "add_into_existing";
    const getMutationRecords = (response: any) => response?.records || response?.data?.records || {};
    const getMutationMessage = (response: any) => response?.message || response?.data?.message || "";
    const getRegistrationLaunchUrl = (response: any) =>
        getMutationRecords(response)?.launchUrl ||
        response?.launchUrl ||
        response?.data?.launchUrl ||
        response?.data;

    const buildRegistrationPayload = (value) => ({
        fname: value?.first_name,
        lname: value?.last_name,
        mname: "",
        mobile: [{ number: value?.mobile }],
        other: [{ number: "" }],
        email: value?.email,
        reEmail: value?.confirm_email,
        password: value?.password,
        reEnterPassword: value?.confirm_password,
        registrationOtp: value?.registration_otp,
        i_confirm: true,
        hearAboutUs: { value: null, label: "Dashboard Registration" },
        url: window.location.href.split("/cp/")[1],
        trn_id: 0,
        subscriberid: data?.data?.records?.subscriber_id,
        propertyid: value?.property?.value || data?.data?.records?.property?.[0]?.ID || 0,
        officeid:
            data?.data?.records?.Office?.ID ||
            (splitData && splitData?.includes("Agent") ? value?.office?.value : 0) ||
            0,
        floorid: data?.data?.records?.select_floor?.ID || 0,
        unitid: data?.data?.records?.selected_unit?.ID || 0,
        Office:
            splitData && splitData?.includes("Agent")
                ? value?.office
                : data?.data?.records?.Office
                    ? { value: data?.data?.records?.Office?.ID, label: data?.data?.records?.Office?.OfficeName }
                    : null,
        agent:
            data?.data?.records?.selected_agent?.FullName &&
            data?.data?.records?.selected_agent?.ASPNetUserID &&
            data?.data?.records?.selected_agent?.UserID
                ? {
                    label: data?.data?.records?.selected_agent?.FullName,
                    value: data?.data?.records?.selected_agent?.ASPNetUserID,
                    id: data?.data?.records?.selected_agent?.UserID,
                }
                : splitData && splitData?.includes("Office")
                    ? value?.agent
                    : { value: null },
        floor: data?.data?.records?.select_floor
            ? {
                label: `Floor Plan: ${data?.data?.records?.select_floor?.FloorCode}`,
                value: data?.data?.records?.select_floor?.ID,
            }
            : null,
        unit: data?.data?.records?.selected_unit
            ? {
                label: `Unit: ${data?.data?.records?.selected_unit?.UnitName} (Rent: $${data?.data?.records?.selected_unit?.MaximumRent})`,
                value: data?.data?.records?.selected_unit?.ID,
            }
            : null,
    });

    const buildLegacyVrPayload = (value) => ({
        invite_type: "Email",
        application_type: (splitData || []).some(
            (segment) => String(segment || "").toLowerCase() === "docupload"
        ) ? "Doc Upload" : "Guest Card",
        subscriberid: data?.data?.records?.subscriber_id,
        is_testapplication: 0,
        office: data?.data?.records?.Office?.OfficeName && data?.data?.records?.Office?.ID
            ? { label: data.data.records.Office.OfficeName, value: data.data.records.Office.ID }
            : (splitData || []).some((segment) => String(segment || "").toLowerCase() === "agent")
                ? value?.office : null,
        property: value?.property,
        agent: data?.data?.records?.selected_agent?.FullName &&
            data?.data?.records?.selected_agent?.ASPNetUserID &&
            data?.data?.records?.selected_agent?.UserID
            ? {
                label: data.data.records.selected_agent.FullName,
                value: data.data.records.selected_agent.ASPNetUserID,
                id: data.data.records.selected_agent.UserID,
            }
            : (splitData || []).some((segment) => String(segment || "").toLowerCase() === "office")
                ? value?.agent : null,
        floor: data?.data?.records?.select_floor
            ? { label: data.data.records.select_floor.FloorCode, value: data.data.records.select_floor.ID } : null,
        unit: data?.data?.records?.selected_unit
            ? { label: data.data.records.selected_unit.UnitName, value: data.data.records.selected_unit.ID } : null,
        applicant: [{
            first_name: value?.first_name,
            last_name: value?.last_name,
            email: value?.email,
            confirm_email: value?.confirm_email,
            username: null,
            mobile_number: value?.mobile,
            sentinvite_email: (splitData || []).some(
                (segment) => String(segment || "").toLowerCase() === "guestcard"
            ) ? 0 : 1,
            sentinvite_sms: 0,
            role: "applicant",
            country_code: value?.country_code,
        }],
        is_behalfofapplicant: null,
        createdby: null,
        desired_propertyaddress: null,
        desired_city: null,
        desired_state: null,
        desired_moveindate: null,
        desired_rent: null,
        desired_unitno: null,
        hearaboutus: null,
        other_lead_source: null,
    });

    const showError = (message: string) => {
        notify(`Operation fail:${message}`, {
            type: "error",
            anchorOrigin: { vertical: "top", horizontal: "right" },
        });
    };

    // form onsubmit Handler
    const onSubmit = (value) => {
        if (isLegacyVrCopyUrl) {
            create("addnewapplication", { data: buildLegacyVrPayload(value) }, {
                onSuccess: (response: any) => {
                    const launchUrl = typeof response?.data === "string"
                        ? response.data
                        : typeof response?.data?.data === "string"
                            ? response.data.data
                            : getRegistrationLaunchUrl(response);
                    notify("Request processed successfully.", {
                        type: "success",
                        anchorOrigin: { vertical: "top", horizontal: "right" },
                    });
                    if (!launchUrl) {
                        showError("Unable to open the DocUpload invitation.");
                        return;
                    }
                    openWithPopupCheck(launchUrl, "_self", undefined, {
                        onBlocked: notifyPopupBlocked(notify),
                    });
                    refresh();
                },
                onError: (e: any) => showError(e.message),
            });
            return;
        }

        const payload = buildRegistrationPayload(value);

        if (!registrationSessionToken) {
            create(
                "registrationSendOtp",
                { data: payload },
                {
                    onSuccess: (response: any) => {
                        const responseRecords = getMutationRecords(response);
                        setRegistrationSessionToken("otp-requested");
                        setOtpVerified(false);
                        setExistingApplications([]);
                        setResultType("new");
                        setSelectedApplicationNumber(null);
                        setSelectedApplicationId(null);
                        setSelectedAction("create_new");
                        setOtpDestination(responseRecords?.destination || "");
                        notify(getMutationMessage(response) || `OTP sent successfully.`, {
                            type: "success",
                            anchorOrigin: { vertical: "top", horizontal: "right" },
                        });
                    },
                    onError: (e: any) => showError(e.message),
                }
            );
            return;
        }

        if (!otpVerified) {
            if (!value?.registration_otp) {
                showError("Enter OTP to continue.");
                return;
            }

            create(
                "registrationVerifyOtp",
                {
                    data: {
                        ...payload,
                        otp: value.registration_otp,
                    },
                },
                {
                    onSuccess: (response: any) => {
                        const responseRecords = getMutationRecords(response);
                        const nextResultType = responseRecords?.resultType || "new";
                        const applications = responseRecords?.existingApplications || [];
                        setOtpVerified(true);
                        setResultType(nextResultType);
                        setExistingApplications(applications);
                        setSelectedApplicationNumber(null);
                        setSelectedApplicationId(null);
                        setSelectedAction("create_new");
                        notify(getMutationMessage(response) || `OTP verified successfully.`, {
                            type: "success",
                            anchorOrigin: { vertical: "top", horizontal: "right" },
                        });
                    },
                    onError: (e: any) => showError(e.message),
                }
            );
            return;
        }

        create(
            "registrationComplete",
            {
                data: {
                    ...payload,
                    action: resultType === "new" ? "create_new" : selectedAction,
                    selectedApplicationNumber,
                    selectedApplicationId,
                },
            },
                {
                    onSuccess: (response: any) => {
                        const launchUrl = getRegistrationLaunchUrl(response);
                        notify(getMutationMessage(response) || `Request processed successfully.`, {
                            type: "success",
                            anchorOrigin: { vertical: "top", horizontal: "right" },
                        });
                        if (!launchUrl) {
                            showError("Unable to open the selected application.");
                            return;
                        }
                        openWithPopupCheck(launchUrl, "_self", undefined, {
                            onBlocked: notifyPopupBlocked(notify),
                        });
                        refresh();
                    },
                    onError: (e: any) => showError(e.message),
                }
        );
    };

    const handleSelectExistingApplication = (application: any = null) => {
        if (!application) {
            setSelectedApplicationNumber(null);
            setSelectedApplicationId(null);
            setSelectedAction("create_new");
            return;
        }

        setSelectedApplicationNumber(Number(application.applicationNumber || 0) || null);
        setSelectedApplicationId(getExistingApplicationSelectionId(application) || null);
        setSelectedAction("continue_existing");
    };


    return (
        <>
            <section className="register-wrapper">
                <div className="register-page">
                    <div className="register-block">
                        <div className="register-header">
                            <div className="text-center">

                                <img
                                    className="mb-3"
                                    src={`${data?.data?.records?.property?.[0]?.LogoURL || data?.data?.records?.Office?.LogoURL}`}
                                    alt={data?.data?.records.subscriber_name}
                                    title={data?.data?.records.subscriber_name}
                                    style={{
                                        maxHeight: "180px",
                                        maxWidth: "50px",
                                        height: "auto",
                                        marginRight: "5px",
                                        display: data?.data?.records.subscriber_name
                                            ? "initial"
                                            : "none", // Hide the entire image if the source is falsy
                                    }}
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement; // Cast to HTMLImageElement
                                        target.style.display = "none"; // Hide the broken image
                                    }}
                                />
                                <p>
                                    Thanks for your interest in{" "}
                                    {splitData?.includes("Agent") ||
                                    splitData?.includes("Office") ? (
                                        <br/>
                                    ) : (
                                        ""
                                    )}
                                    {data?.data?.records?.property?.[0]?.PropertyName ||
                                        data?.data?.records?.subscriber_name}.
                                    <br/>
                                    {splitData?.includes("Unit") ? (
                                        <b>{`Unit: ${data?.data?.records?.selected_unit?.UnitName} (Rent: $${data?.data?.records?.selected_unit?.MaximumRent})`}</b>
                                    ) : splitData?.includes("Floor") ? (
                                        <b>{`Floor Plan: ${data?.data?.records?.select_floor?.FloorCode} (Min Rent: $${data?.data?.records?.select_floor?.MinimumRent}, Max Rent: $${data?.data?.records?.select_floor?.MaximumRent})`}</b>
                                    ) : (
                                        ""
                                    )}
                                </p>
                            </div>

                            <div className="text-center mr-b30 mr-t10">
                                <br/>
                                <p className="mr-b10">
                                    Please proceed to securely verify your documents through Docuverus.
                                </p>
                                {/* <h6>
                  Already started application?{" "}
                  <NavLink to="/ats/login">Login Here</NavLink>
                </h6> */}
                                <br/>
                            </div>
                        </div>

                        <div className="register-form mr-t30">
                            <FormProvider {...methods}>
                                <form onSubmit={methods.handleSubmit(onSubmit)}>
                                    <div className="row">
                                        <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                            <InputField2
                                                label="First Name"
                                                source={`first_name`}
                                                placeholder="Enter First Name"
                                                labelClass={"lable-title"}
                                                additionalInputClass={"cus-control"}
                                                disable={Boolean(registrationSessionToken)}
                                            />
                                        </div>
                                        <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                            <InputField2
                                                label="Last Name"
                                                source={`last_name`}
                                                placeholder="Enter Last Name"
                                                labelClass={"lable-title"}
                                                additionalInputClass={"cus-control"}
                                                disable={Boolean(registrationSessionToken)}
                                            />
                                        </div>
                                        <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                            <TextInputPhone
                                                source={`mobile`}
                                                label="Mobile"
                                                isCountryCode={`country_code`}
                                                labelClass={"lable-title"}
                                                additionalInputClass={"cus-control"}
                                                widthClass={"w-100"}
                                                requiredClass={{
                                                    fontSize: "70%",
                                                }}
                                                disabled={Boolean(registrationSessionToken)}
                                                // required={false}
                                            />
                                        </div>
                                        <div className="col-sm-12 col-md-6 col-lg-6"></div>
                                        <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                            <InputField2
                                                label="Email"
                                                source={`email`}
                                                placeholder="Enter Email"
                                                labelClass={"lable-title"}
                                                additionalInputClass={"cus-control"}
                                                disable={Boolean(registrationSessionToken)}
                                                // handleOnChange={() => methods.trigger("confirm_email")}
                                            />
                                        </div>
                                        <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                            <InputField2
                                                label="Confirm Email"
                                                source={`confirm_email`}
                                                placeholder="Enter Confirm Email"
                                                labelClass={"lable-title"}
                                                additionalInputClass={"cus-control"}
                                                disable={Boolean(registrationSessionToken)}
                                            />
                                        </div>
                                        {!isLegacyVrCopyUrl && <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                            <InputField2
                                                label="Password"
                                                source={`password`}
                                                type="password"
                                                placeholder="Enter Password"
                                                labelClass={"lable-title"}
                                                additionalInputClass={"cus-control"}
                                                disable={Boolean(registrationSessionToken)}
                                                maskInReplay={true}
                                            />
                                        </div>}
                                        {!isLegacyVrCopyUrl && <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                            <InputField2
                                                label="Confirm Password"
                                                source={`confirm_password`}
                                                type="password"
                                                placeholder="Enter Confirm Password"
                                                labelClass={"lable-title"}
                                                additionalInputClass={"cus-control"}
                                                disable={Boolean(registrationSessionToken)}
                                                maskInReplay={true}
                                            />
                                        </div>}
                                        {!isLegacyVrCopyUrl && registrationSessionToken && !otpVerified && (
                                            <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                                <InputField2
                                                    label={`OTP${otpDestination ? ` (${otpDestination})` : ""}`}
                                                    source={`registration_otp`}
                                                    placeholder="Enter OTP"
                                                    labelClass={"lable-title"}
                                                    additionalInputClass={"cus-control"}
                                                />
                                            </div>
                                        )}

                                        {splitData &&
                                            splitData?.length > 0 &&
                                            splitData?.includes("Agent") && (
                                                <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                                    <InputSelectField
                                                        label="Office"
                                                        source={`office`}
                                                        options={data?.data?.records?.Office?.map(
                                                            (item) => ({
                                                                label: item.OfficeName,
                                                                value: item.ID,
                                                            })
                                                        )}
                                                        disable={Boolean(registrationSessionToken)}
                                                        labelAsteriskClass={"color-red"}
                                                        labelClass={"lable-title"}
                                                        requiredClass={{
                                                            fontSize: "70%",
                                                        }}
                                                    />
                                                </div>
                                            )}

                                        {data?.data?.records?.subscriberCompanyType === 'Property Management' && (
                                            <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                                <InputSelectField
                                                    stopAutoFocus={true}  // Don't have this be default focus since it shouldn't change.
                                                    label="Property"
                                                    source={`property`}
                                                        options={data?.data?.records?.property?.map(
                                                            (item) => ({
                                                                label: item.PropertyName,
                                                                value: item.ID,
                                                            })
                                                        )}
                                                        disable={Boolean(registrationSessionToken)}
                                                        labelAsteriskClass={"color-red"}
                                                        labelClass={"lable-title"}
                                                    requiredClass={{
                                                        fontSize: "70%",
                                                    }}
                                                />
                                                {data?.data?.records?.subscriber_name && (
                                                    <small className="text-muted">
                                                        {data.data.records.subscriber_name}
                                                    </small>
                                                )}
                                            </div>
                                        )}
                                        {splitData &&
                                            splitData?.length > 0 &&
                                            splitData?.includes("Office") && (
                                                <div className="col-sm-12 col-md-6 col-lg-6 mr-b10">
                                                    <InputSelectField
                                                        label="Agent"
                                                        source={`agent`}
                                                        options={data?.data?.records?.Agents?.map(
                                                            (item) => ({
                                                                label: item.FullName,
                                                                value: item.ASPNetUserID,
                                                                id: item.UserID,
                                                            })
                                                        )}
                                                        disable={Boolean(registrationSessionToken)}
                                                        labelAsteriskClass={"color-red"}
                                                        labelClass={"lable-title"}
                                                        requiredClass={{
                                                            fontSize: "70%",
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        {otpVerified && resultType !== "new" && (
                                            <div className="col-sm-12 mr-b10">
                                                <div className="blue-in-box p-3">
                                                    <div className="mr-b10">
                                                        <strong>Existing applications found</strong>
                                                    </div>
                                                    {existingApplications?.map((application) => (
                                                        <div
                                                            className="form-check mb-2"
                                                            key={`${application.applicationNumber}-${getExistingApplicationSelectionId(application)}`}
                                                        >
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="dashboardExistingApplicationNumber"
                                                                checked={
                                                                    Number(selectedApplicationId || 0) > 0
                                                                        ? getExistingApplicationSelectionId(application) === Number(selectedApplicationId)
                                                                        : Number(selectedApplicationNumber) === Number(application.applicationNumber)
                                                                }
                                                                onChange={() => handleSelectExistingApplication(application)}
                                                            />
                                                            <label className="form-check-label">
                                                                #{application.applicationNumber} {application.applicantName}
                                                            </label>
                                                        </div>
                                                    ))}
                                                    {existingApplications?.length === 0 && (
                                                        <div className="text-muted">
                                                            No existing applications are available.
                                                        </div>
                                                    )}
                                                    <div className="mr-b10">
                                                        {!hasSelectedExistingApplication ? (
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    checked={selectedAction === "create_new"}
                                                                    onChange={() => handleSelectExistingApplication()}
                                                                />
                                                                {" "}Start new
                                                            </label>
                                                        ) : (
                                                            <>
                                                                <label className="me-3">
                                                                    <input
                                                                        type="radio"
                                                                        checked={selectedAction === "continue_existing"}
                                                                        onChange={() => setSelectedAction("continue_existing")}
                                                                    />
                                                                    {" "}Continue existing
                                                                </label>
                                                                {selectedApplicationAllowsAddIntoExisting && (
                                                                    <label className="me-3">
                                                                        <input
                                                                            type="radio"
                                                                            checked={selectedAction === "add_into_existing"}
                                                                            onChange={() => setSelectedAction("add_into_existing")}
                                                                        />
                                                                        {" "}Add as co-applicant
                                                                    </label>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0 align-baseline"
                                                                    onClick={() => handleSelectExistingApplication()}
                                                                >
                                                                    Start new instead
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex justify-content-center align-items-center">
                                        <button
                                            type="submit"
                                            className="l-btn w-50"
                                            disabled={
                                                isLoading ||
                                                (requiresExistingApplicationSelection && !selectedApplicationNumber && !selectedApplicationId) ||
                                                (selectedAction === "add_into_existing" && !selectedApplicationAllowsAddIntoExisting)
                                            }
                                        >
                                            {isLegacyVrCopyUrl
                                                ? "Start"
                                                : !registrationSessionToken
                                                ? "Send OTP"
                                                : !otpVerified
                                                    ? "Verify OTP"
                                                    : resultType === "new" || selectedAction === "create_new"
                                                        ? "Start New"
                                                        : selectedAction === "add_into_existing"
                                                            ? "Add as Co-applicant"
                                                            : "Continue"}
                                        </button>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>

                        <div className="l-foot-links mr-t20">
                            <ul className="mx-auto">
                                <li>
                                    <a href={"/atss/DataPolicy"} target="_blank" rel="noreferrer">
                                        Data Policy
                                    </a>
                                </li>

                                <li>
                                    <a href={"/atss/TermsOfUse"} target="_blank" rel="noreferrer">
                                        Terms of Use
                                    </a>
                                </li>
                                {/* <li>Dev Version 1.0.0.9</li> */}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};
